/* Bulk-scan session: the pure core of the scanner.
 *
 * Everything decidable without a camera lives here, deliberately, because a
 * browser camera cannot be unit-tested and the rules that matter (which read
 * wins, what counts as a duplicate, what a typed serial looks like) all can be.
 *
 * Duplicate policy: FIRST READ WINS. At a warehouse the same label passes the
 * lens dozens of times per second and pallets get re-scanned; the second read
 * of a serial is noise, not a correction. Suppressed reads are counted, per
 * serial and in total, and the total is shown — silent suppression would look
 * like the scanner had stopped working.
 */

import {
  DEFAULT_SERIAL_RULES,
  type RejectReason,
  type ScanEvent,
  type ScanSessionState,
  type SerialCandidate,
  type SerialCapture,
  type SerialRules,
} from './types';

export function emptySession(): ScanSessionState {
  return { captures: [], duplicates: 0, duplicatesBySerial: {}, lastEvent: null };
}

/**
 * Normalise a raw read into the value that will be written.
 *
 * Intentionally conservative: trim, strip surrounding control characters and
 * ALL internal whitespace, upper-case. Nothing else. In particular it does NOT
 * map O->0 or I->1: a serial is the primary key of a physical asset, and a
 * silent glyph substitution produces a record that can never be reconciled
 * against the unit on the wall. Ambiguity is the confirmation UI's job.
 */
export function normaliseSerial(raw: string): string {
  return raw
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, '')
    .replace(/\s+/g, '')
    .trim()
    .toUpperCase();
}

export type Validation =
  | { ok: true; serial: string }
  | { ok: false; reason: RejectReason };

export function validateSerial(
  raw: string,
  rules: SerialRules = DEFAULT_SERIAL_RULES,
): Validation {
  const serial = normaliseSerial(raw);
  if (serial.length === 0) return { ok: false, reason: 'empty' };
  if (serial.length < rules.minLength) return { ok: false, reason: 'too-short' };
  if (serial.length > rules.maxLength) return { ok: false, reason: 'too-long' };
  if (!rules.allowed.test(serial)) return { ok: false, reason: 'charset' };
  return { ok: true, serial };
}

/**
 * Offer a candidate to the session. Same function for scans and manual entry —
 * that is the whole point: one code path, so the two cannot drift apart and a
 * typed serial cannot end up shaped differently from a scanned one.
 *
 * Returns a new state; never mutates.
 */
export function offer(
  state: ScanSessionState,
  candidate: SerialCandidate,
  rules: SerialRules = DEFAULT_SERIAL_RULES,
): ScanSessionState {
  const v = validateSerial(candidate.raw, rules);
  if (!v.ok) {
    return {
      ...state,
      lastEvent: { kind: 'rejected', raw: candidate.raw, reason: v.reason },
    };
  }

  const existing = state.captures.find((c) => c.serial === v.serial);
  if (existing) {
    const count = (state.duplicatesBySerial[v.serial] ?? 0) + 1;
    return {
      captures: state.captures,
      duplicates: state.duplicates + 1,
      duplicatesBySerial: { ...state.duplicatesBySerial, [v.serial]: count },
      lastEvent: { kind: 'duplicate', serial: v.serial, ofId: existing.id, count },
    };
  }

  const capture: SerialCapture = {
    id: candidate.id,
    serial: v.serial,
    raw: candidate.raw,
    method: candidate.method,
    format: candidate.method === 'scan' ? (candidate.format ?? null) : null,
    at: candidate.at,
  };

  return {
    captures: [...state.captures, capture],
    duplicates: state.duplicates,
    duplicatesBySerial: state.duplicatesBySerial,
    lastEvent: { kind: 'accepted', capture },
  };
}

/**
 * Remove a capture the technician says is wrong.
 *
 * The duplicate counters are left alone on purpose: they record what the
 * scanner saw, and a removal does not un-see it. Clearing them would make the
 * "12 duplicates suppressed" line disagree with what actually happened.
 */
export function removeCapture(state: ScanSessionState, id: string): ScanSessionState {
  const captures = state.captures.filter((c) => c.id !== id);
  if (captures.length === state.captures.length) return state;
  return { ...state, captures, lastEvent: null };
}

/** Replace a capture's serial (the "no — type it" correction path). */
export function editCapture(
  state: ScanSessionState,
  id: string,
  raw: string,
  at: number,
  rules: SerialRules = DEFAULT_SERIAL_RULES,
): { state: ScanSessionState; event: ScanEvent } {
  const v = validateSerial(raw, rules);
  if (!v.ok) {
    const event: ScanEvent = { kind: 'rejected', raw, reason: v.reason };
    return { state: { ...state, lastEvent: event }, event };
  }
  const clash = state.captures.find((c) => c.serial === v.serial && c.id !== id);
  if (clash) {
    const count = (state.duplicatesBySerial[v.serial] ?? 0) + 1;
    const event: ScanEvent = {
      kind: 'duplicate',
      serial: v.serial,
      ofId: clash.id,
      count,
    };
    return {
      state: {
        ...state,
        duplicates: state.duplicates + 1,
        duplicatesBySerial: { ...state.duplicatesBySerial, [v.serial]: count },
        lastEvent: event,
      },
      event,
    };
  }

  let edited: SerialCapture | null = null;
  const captures = state.captures.map((c) => {
    if (c.id !== id) return c;
    // An edited value is a typed value, whatever it started life as.
    edited = { ...c, serial: v.serial, raw, method: 'manual', format: null, at };
    return edited;
  });
  if (!edited) return { state, event: { kind: 'rejected', raw, reason: 'empty' } };
  const event: ScanEvent = { kind: 'accepted', capture: edited };
  return { state: { ...state, captures, lastEvent: event }, event };
}

/** Counts for the running header. */
export function sessionCounts(state: ScanSessionState): {
  unique: number;
  duplicates: number;
  scanned: number;
  typed: number;
} {
  let scanned = 0;
  let typed = 0;
  for (const c of state.captures) {
    if (c.method === 'scan') scanned += 1;
    else typed += 1;
  }
  return { unique: state.captures.length, duplicates: state.duplicates, scanned, typed };
}

export function hasSerial(state: ScanSessionState, raw: string): boolean {
  const s = normaliseSerial(raw);
  return state.captures.some((c) => c.serial === s);
}
